import { useEffect } from 'react';
import { getSiteUrl } from '../utils/siteConfig';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noindex?: boolean;
}

/**
 * SEOHead Component
 *
 * Dynamically updates document head meta tags for SEO.
 * Use this component in each page to set page-specific SEO metadata.
 */
export function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noindex = false,
}: SEOHeadProps) {
  const resolvedOgImage = ogImage || `${getSiteUrl()}/og-image.png`;
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update/create meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag(
        'robots',
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      );
    }

    // Open Graph
    updateMetaProperty('og:title', title);
    updateMetaProperty('og:description', description);
    updateMetaProperty('og:type', ogType);
    updateMetaProperty('og:image', resolvedOgImage);
    updateMetaProperty('og:locale', 'vi_VN');
    updateMetaProperty('og:site_name', 'Hướng nghiệp CAT - Career Aptitude Test');
    if (canonicalUrl) updateMetaProperty('og:url', canonicalUrl);

    // Twitter
    updateMetaProperty('twitter:title', title);
    updateMetaProperty('twitter:description', description);
    updateMetaProperty('twitter:image', resolvedOgImage);
    updateMetaProperty('twitter:card', 'summary_large_image');

    // Canonical URL
    updateCanonicalLink(canonicalUrl);

    // Cleanup function is not needed as we want meta tags to persist
  }, [title, description, keywords, canonicalUrl, resolvedOgImage, ogType, noindex]);

  return null;
}

function updateMetaTag(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function updateMetaProperty(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function updateCanonicalLink(url?: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (url) {
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  } else if (link) {
    link.remove();
  }
}

export default SEOHead;
